/**
 * SendSMSModal Component
 * Modal for composing and sending SMS messages to customers or subcontractors
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Send, MessageSquare, User, Phone, Loader2, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { sendSMS } from '../services/sms'
import { getTemplates } from '../services/smsTemplates'
import type { RecipientType } from '../types/communication'

interface SendSMSModalProps {
  isOpen: boolean
  onClose: () => void
  recipientType: RecipientType
  recipientId: string
  recipientName: string
  recipientPhone: string
  assignmentId?: string
  onMessageSent?: () => void
  initialMessage?: string
}

// Template variable definitions for documentation
export const TEMPLATE_VARIABLES = [
  { key: '{name}', description: 'Mottagarens förnamn', always: true },
  { key: '{fullname}', description: 'Mottagarens fullständiga namn', always: true },
  { key: '{avsändare}', description: 'Ditt namn (inloggad användare)', always: true },
  { key: '{uppdragsnr}', description: 'Uppdragsnummer (t.ex. #1001)', always: false },
  { key: '{datum}', description: 'Datum (t.ex. 2025-12-26)', always: false },
  { key: '{tid}', description: 'Starttid', always: false },
  { key: '{location}', description: 'Uppdragets plats', always: false },
  { key: '{titel}', description: 'Uppdragstitel', always: false },
  { key: '{trädgårdsmästare}', description: 'Tilldelad trädgårdsmästare', always: false },
]


export function SendSMSModal({
  isOpen,
  onClose,
  recipientType,
  recipientId,
  recipientName,
  recipientPhone,
  assignmentId,
  onMessageSent,
  initialMessage
}: SendSMSModalProps) {
  const { profile } = useAuth()
  const [message, setMessage] = useState(initialMessage || '')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(initialMessage ? 'custom' : null)
  const [isSending, setIsSending] = useState(false)
  const [charCount, setCharCount] = useState(initialMessage?.length || 0)
  const [showVariables, setShowVariables] = useState(false)

  // Reset message when modal opens with initialMessage
  useEffect(() => {
    if (isOpen && initialMessage) {
      setMessage(initialMessage)
      setCharCount(initialMessage.length)
      setSelectedTemplate('custom')
    }
  }, [isOpen, initialMessage])

  // Fetch templates from database, filtered by recipient type
  const { data: dbTemplates = [] } = useQuery({
    queryKey: ['sms-templates', recipientType],
    queryFn: () => getTemplates(recipientType),
    enabled: isOpen
  })

  // Fetch assignment data if assignmentId is provided
  const { data: assignment } = useQuery({
    queryKey: ['assignment-sms', assignmentId],
    queryFn: async () => {
      if (!assignmentId) return null
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          assignment_number,
          title,
          start_date,
          start_time,
          location,
          assignment_subcontractors(
            subcontractors(name)
          )
        `)
        .eq('id', assignmentId)
        .single()

      if (error) {
        console.error('Error fetching assignment:', error)
        return null
      }
      return data
    },
    enabled: isOpen && !!assignmentId
  })

  // Map database templates for display
  const templates = dbTemplates.map(t => ({ id: t.id, name: t.name, content: t.content }))

  // SMS character limits
  const SMS_CHAR_LIMIT = 160
  const SMS_EXTENDED_LIMIT = 480 // 3 SMS messages

  // Replace all template variables with actual values
  const replaceTemplateVariables = (text: string): string => {
    let result = text

    // Always available variables
    const firstName = recipientName.split(' ')[0]
    result = result.replace(/{name}/g, firstName)
    result = result.replace(/{fullname}/g, recipientName)

    // Sender name (logged in user)
    const senderName = profile?.full_name || ''
    const senderFirstName = senderName.split(' ')[0]
    result = result.replace(/{avsändare}/g, senderFirstName)

    // Assignment-specific variables
    if (assignment) {
      // Assignment number (e.g. #1001)
      const assignmentNumber = assignment.assignment_number
        ? `#${assignment.assignment_number}`
        : ''
      result = result.replace(/{uppdragsnr}/g, assignmentNumber)

      // Date in ISO format (YYYY-MM-DD)
      result = result.replace(/{datum}/g, assignment.start_date || '')

      // Start time only
      result = result.replace(/{tid}/g, assignment.start_time || '')

      // Address from assignment location (can be string or object with address property)
      const location = assignment.location
      const locationAddress = typeof location === 'string'
        ? location
        : (location as { address?: string } | null)?.address || ''
      result = result.replace(/{location}/g, locationAddress)

      // Title
      result = result.replace(/{titel}/g, assignment.title || '')

      // Subcontractor names
      const subcontractors = assignment.assignment_subcontractors as unknown as Array<{ subcontractors: { name: string } | null }> | null
      const subcontractorNames = subcontractors
        ?.map(as => as.subcontractors?.name)
        .filter(Boolean)
        .join(', ') || ''
      result = result.replace(/{trädgårdsmästare}/g, subcontractorNames)
    } else {
      // Clear assignment variables if no assignment context
      result = result.replace(/{uppdragsnr}/g, '')
      result = result.replace(/{datum}/g, '')
      result = result.replace(/{tid}/g, '')
      result = result.replace(/{location}/g, '')
      result = result.replace(/{titel}/g, '')
      result = result.replace(/{trädgårdsmästare}/g, '')
    }

    return result
  }

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId)
    if (template) {
      setSelectedTemplate(templateId)
      const personalizedMessage = replaceTemplateVariables(template.content)
      setMessage(personalizedMessage)
      setCharCount(personalizedMessage.length)
    }
  }

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    setMessage(text)
    setCharCount(text.length)
    setSelectedTemplate('custom')
  }

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Skriv ett meddelande')
      return
    }

    if (!recipientPhone) {
      toast.error('Mottagaren har inget telefonnummer')
      return
    }

    setIsSending(true)

    try {
      // Replace any variables in the message before sending
      const finalMessage = replaceTemplateVariables(message.trim())

      const result = await sendSMS({
        recipient_type: recipientType,
        recipient_id: recipientId,
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
        message: finalMessage,
        assignment_id: assignmentId
      })

      if (result.success) {
        toast.success('SMS skickat!')
        setMessage('')
        setSelectedTemplate(null)
        setCharCount(0)
        onMessageSent?.()
        onClose()
      } else {
        toast.error(result.error || 'Kunde inte skicka SMS')
      }
    } catch (error) {
      toast.error('Ett fel uppstod')
      console.error('Error sending SMS:', error)
    } finally {
      setIsSending(false)
    }
  }

  if (!isOpen) return null

  const getCharCountColor = () => {
    if (charCount > SMS_EXTENDED_LIMIT) return 'text-red-500'
    if (charCount > SMS_CHAR_LIMIT) return 'text-yellow-500'
    return 'text-nature-500'
  }

  const getSmsCount = () => {
    if (charCount === 0) return 0
    return Math.ceil(charCount / SMS_CHAR_LIMIT)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Skicka SMS
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  Till {recipientType === 'customer' ? 'kund' : 'trädgårdsmästare'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Recipient Info */}
          <div className="bg-nature-50 dark:bg-nature-700 rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-[#faf9f7] dark:bg-nature-600 rounded-full">
                <User className="h-5 w-5 text-nature-600 dark:text-nature-300" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-nature-900 dark:text-nature-100">
                  {recipientName}
                </p>
                <div className="flex items-center space-x-1 text-sm text-nature-500 dark:text-nature-400">
                  <Phone className="h-3 w-3" />
                  <span>{recipientPhone || 'Inget nummer'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Message Templates */}
          <div>
            <label className="form-label">Välj mall</label>
            <div className="grid grid-cols-2 gap-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                    selectedTemplate === template.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-nature-200 dark:border-nature-600 hover:border-nature-300 dark:hover:border-nature-500 text-nature-700 dark:text-nature-300'
                  }`}
                >
                  {template.name}
                </button>
              ))}
              <button
                onClick={() => {
                  setSelectedTemplate('custom')
                  setMessage('')
                  setCharCount(0)
                }}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                  selectedTemplate === 'custom'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-nature-200 dark:border-nature-600 hover:border-nature-300 dark:hover:border-nature-500 text-nature-700 dark:text-nature-300'
                }`}
              >
                Eget meddelande
              </button>
            </div>
          </div>

          {/* Message Input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="form-label mb-0">Meddelande</label>
              <button
                type="button"
                onClick={() => setShowVariables(!showVariables)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              >
                <Info className="h-3 w-3" />
                Variabler
              </button>
            </div>

            {/* Variables help */}
            {showVariables && (
              <div className="mb-2 p-3 bg-nature-50 dark:bg-nature-700 rounded-lg border border-nature-200 dark:border-nature-600">
                <p className="text-xs font-medium text-nature-700 dark:text-nature-300 mb-2">
                  Tillgängliga variabler:
                </p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {TEMPLATE_VARIABLES.filter(v => v.always || assignmentId).map((v) => (
                    <div key={v.key} className="flex items-center gap-2">
                      <code className="px-1 py-0.5 bg-nature-200 dark:bg-nature-600 rounded text-nature-800 dark:text-nature-200">
                        {v.key}
                      </code>
                      <span className="text-nature-500 dark:text-nature-400">{v.description}</span>
                    </div>
                  ))}
                </div>
                {!assignmentId && (
                  <p className="text-xs text-nature-500 dark:text-nature-400 mt-2 italic">
                    Fler variabler tillgängliga vid uppdragskontext
                  </p>
                )}
              </div>
            )}

            <textarea
              value={message}
              onChange={handleMessageChange}
              placeholder="Skriv ditt meddelande här..."
              rows={5}
              className="input-field resize-none"
              maxLength={SMS_EXTENDED_LIMIT}
            />
            <div className="flex items-center justify-between mt-2">
              <span className={`text-sm ${getCharCountColor()}`}>
                {charCount} / {SMS_CHAR_LIMIT} tecken
                {getSmsCount() > 1 && (
                  <span className="ml-2 text-nature-500 dark:text-nature-400">
                    ({getSmsCount()} SMS)
                  </span>
                )}
              </span>
              {charCount > SMS_EXTENDED_LIMIT && (
                <span className="text-sm text-red-500">
                  Meddelandet är för långt
                </span>
              )}
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              SMS skickas direkt till mottagarens telefon. Svar sparas automatiskt i konversationshistoriken.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="btn-outline px-4 py-2"
              disabled={isSending}
            >
              Avbryt
            </button>
            <button
              onClick={handleSend}
              disabled={isSending || !message.trim() || charCount > SMS_EXTENDED_LIMIT || !recipientPhone}
              className="btn-primary px-4 py-2 flex items-center space-x-2"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Skickar...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>Skicka SMS</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
